import React, { useEffect } from 'react';
import { WidthProvider, Responsive } from 'react-grid-layout'
import EventMap from '../charts-graphs/map';
import CarLocateHandler from '../handlers/carLocate-handler';
import ResetButton from '../resetButton';
import { useAtom } from 'jotai';
import { carLocationsAtom } from '../atoms/carLocationsAtom';
import { allCarsAtom } from '../atoms/allCarsAtom';
import useWebSocket from '../wsHook';
import { StatusUpdate, UIDUpdate } from '../atoms/allCarsAtom';

const ResponsiveGridLayout = WidthProvider(Responsive);

const MapGrid: React.FC = () => {
  const [, setCarLocations] = useAtom(carLocationsAtom);
  const [, setAllCars] = useAtom(allCarsAtom);
  const { messages } = useWebSocket<StatusUpdate|UIDUpdate>(['status', 'uid']);

  useEffect(() => {
    if (messages.length > 0) {
      const latestMessage = messages[messages.length - 1];
      if (latestMessage.channel === 'status') {
        const { CarNumber, Status } = latestMessage.message as StatusUpdate;
        
        // Update carLocationsAtom
        setCarLocations(prevLocations => {
          if (!prevLocations) return prevLocations;
          return prevLocations.map(loc => 
            loc.CarNumber === CarNumber 
              ? { ...loc, Status } 
              : loc
          );
        });

        // Update allCarsAtom
        setAllCars(prevCars => {
          if (!prevCars) return prevCars;
          return prevCars.map(car => 
            car.CarNumber === CarNumber 
              ? { ...car, Status } 
              : car
          );
        });
      } else if (latestMessage.channel === 'uid') {
        const message = latestMessage.message as UIDUpdate;
        const { CarNumber, UID } = message;
        
        // Update allCarsAtom with new UID
        setAllCars(prevCars => {
          if (!prevCars) return prevCars;
          return prevCars.map(car => 
            car.CarNumber === CarNumber 
              ? { ...car, UID } 
              : car
          );
        });
      }
    }
  }, [messages, setCarLocations, setAllCars]);

  const layouts = {
    lg: [
      { i: 'a', x: 1, y: 1, w: 3, h: 2.5, minW: 3, minH: 2.5 },
      { i: 'b', x: 1, y: 9, w: 3, h: 20, minW: 3, minH: 20 },
    ],
    md: [
      { i: 'a', x: 1, y: 1, w: 3, h: 2.5, minW: 3, minH: 2.5 },
      { i: 'b', x: 1, y: 9, w: 3, h: 20, minW: 3, minH: 20 },
    ],
    sm: [
      { i: 'a', x: 1, y: 1, w: 3, h: 2.5, minW: 3, minH: 2.5 },
      { i: 'b', x: 1, y: 9, w: 3, h: 20, minW: 3, minH: 20 },
    ],
    xs: [
      { i: 'a', x: 0, y: 1, w: 1, h: 3.5, minW: 1, minH: 3.5 },
      { i: 'b', x: 0, y: 5, w: 1, h: 20, minW: 1, minH: 20 },
    ]
  };

  return (
    <ResponsiveGridLayout 
      className="layout" 
      layouts={layouts}
      breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }} 
      cols={{ lg: 3, md: 3, sm: 3, xs: 1 }}
      rowHeight={100}
      draggableCancel=".cancelSelectorName"
      isDraggable={false}
      isResizable={false}
      >
      <div key="a" style={{ minHeight: "500px" }} className='card-gauge'>
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          <EventMap />
          <div style={{ position: 'absolute', top: '55px', left: '10px', zIndex: 10 }}>
            <ResetButton>Recenter Map</ResetButton>
          </div>
        </div>
      </div>
      <div key="b" style={{ minHeight: "500px" }} className='card-gauge'>
        <CarLocateHandler />
      </div>
    </ResponsiveGridLayout>
  );
};

export default MapGrid;